import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FileV2Info } from '@schenck/rotec-components';
import { of } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, map } from 'rxjs/operators';
import { API_BASE_URL } from './tokens';

export interface FileInfo extends FileV2Info {
  /**
   * The human readable size, not the technical content size.
   */
  fileSize?: string;
}
export interface DeviceFiles {
  bugReportFiles: FileInfo[];
  supportBundleFiles: FileInfo[];
}

@Injectable()
export class FileApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);


  public getDeviceFilesByMachineId$(id: string): Observable<DeviceFiles> {
    return this.http.get<DeviceFiles>(`${this.baseUrl}/devices/${id}/files`).pipe(
      map((deviceFiles: DeviceFiles) => {
        return {
          bugReportFiles: deviceFiles.bugReportFiles.map((file) => ({
            ...file,
            fileSize: this.getFileSize(file),
          })),
          supportBundleFiles: deviceFiles.supportBundleFiles.map((file) => ({
            ...file,
            fileSize: this.getFileSize(file),
          })),
        };
      }),
      catchError(() => of({ bugReportFiles: [], supportBundleFiles: [] }))
    );
  }

  public getMachineFile$(file: { id: string; name: string }, id: string): Observable<File> {
    // There is an issue with getting access to the source by file url. The issue only appears when we deploy to Azure.
    // We need to double-encode the file id to avoid the error
    // See the issue link https://github.com/Azure/azure-functions-host/issues/3998
    const encodedFileId = encodeURIComponent(encodeURIComponent(file.id));

    return this.http
      .get(`${this.baseUrl}/devices/${id}/files/${encodedFileId}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map(
          (response: HttpResponse<Blob>) =>
            new File([response.body], file.name, { type: response.body?.type })
        )
      );
  }

  public deleteMachineFile$(id: string, machineId: string): Observable<boolean> {
    // There is an issue with getting access to the source by file url. The issue only appears when we deploy to Azure.
    // We need to double-encode the file id to avoid the error
    // See the issue link https://github.com/Azure/azure-functions-host/issues/3998
    const encodedFileId = encodeURIComponent(encodeURIComponent(id));

    return this.http
      .delete(`${this.baseUrl}/devices/${machineId}/files/${encodedFileId}`)
      .pipe(map(() => true));
  }

  private getFileSize(file: FileInfo): string {
    const sizeKb = file.size / 1024;

    if (sizeKb < 100) {
      return `${sizeKb.toFixed(2)} Kb`;
    }

    const sizeMb = sizeKb / 1024;

    return `${sizeMb.toFixed(2)} Mb`;
  }
}
